import { useRef } from 'react'

import { useFrame } from '../../engine'
import { anim, pv, seg, smooth, stagger } from '../../motion'
import { useCopy } from '../i18n'
import { CtaPair } from '../play/PlayStory'
import { C, Shape } from '../play/shapes'

/*
 * Szenen von „Aivy Balance“. Pink steht für Zeit mit Menschen, Blau für das,
 * was Aivy übernimmt, Grau für Arbeit an Unterlagen.
 */

const HUMAN = C.pink
const AUTO = C.blue

/** Zwei Versprechen prallen aufeinander und werden zu einem Satz. */
export function Paradox() {
  const c = useCopy('paradox')
  return (
    <div
      className='ba-paradox'
      style={{ ...pv('m', [0.12, 0.4]), ...pv('o', [0.42, 0.56]) }}
    >
      <div className='ba-paradox__pair'>
        <span className='ba-claim ba-claim--human'>
          <Shape kind='circle' color={HUMAN} size={34} />
          {c.human}
        </span>
        <span className='ba-claim ba-claim--auto'>
          <Shape kind='square' color={AUTO} size={34} />
          {c.auto}
        </span>
      </div>
      <span
        className='ba-paradox__clash'
        style={anim({ at: [0.26, 0.34], out: [0.44, 0.5], y: 10 })}
      >
        {c.clash}
      </span>
      <div className='ba-paradox__lead'>
        <h1 className='ap-display'>
          <span className='ba-blue' style={anim({ at: [0.52, 0.6], y: 30 })}>
            {c.lead[0]}
          </span>
          <span className='ba-pink' style={anim({ at: [0.58, 0.66], y: 30 })}>
            {c.lead[1]}
          </span>
        </h1>
        <p className='ap-lead' style={anim({ at: [0.66, 0.74] })}>
          {c.sub}
        </p>
      </div>
    </div>
  )
}

/** Aussagen aus Kundengesprächen, links Mensch, rechts Zeit. */
export function Voices() {
  const c = useCopy('stimmen')
  const ui = useCopy('ui')
  const cols = [
    ['human', c.human, c.humanQuotes, HUMAN, 'circle'],
    ['auto', c.auto, c.autoQuotes, AUTO, 'square']
  ]
  return (
    <div className='ba-voices'>
      <h2 className='ap-h1'>{c.title}</h2>
      <div className='ba-voices__cols'>
        {cols.map(([id, label, quotes, color, kind], col) => (
          <div key={id} className={`ba-voices__col ba-voices__col--${id}`}>
            <span className='ba-chip'>
              <Shape kind={kind} color={color} size={14} />
              {label}
            </span>
            {quotes.map((q, i) => (
              <blockquote
                key={q}
                className='ba-quote'
                style={anim({
                  at: stagger(0.08 + col * 0.06, 0.14, 0.1, i),
                  y: 30
                })}
              >
                „{q}“
              </blockquote>
            ))}
          </div>
        ))}
      </div>
      <span className='ba-foot'>{ui.voices}</span>
    </div>
  )
}

// Stunden je Block in vier Zuständen der Woche, Summe immer 40
const BLOCKS = [
  ['sichten', 'admin'],
  ['unpassend', 'admin'],
  ['termine', 'admin'],
  ['abstimmen', 'admin'],
  ['gespraeche', 'human'],
  ['sourcing', 'human'],
  ['feedback', 'human'],
  ['rest', 'rest']
]
const STATES = [
  [16, 10, 4, 4, 4, 0, 0, 2],
  [2, 10, 4, 4, 8, 6, 4, 2],
  [2, 2, 4, 2, 12, 6, 10, 2],
  [2, 2, 1, 2, 12, 8, 11, 2]
]
const MOVES = [
  [0.16, 0.3],
  [0.38, 0.52],
  [0.6, 0.72]
]
const STEP_WIN = [
  [0.12, 0.34],
  [0.34, 0.56],
  [0.56, 0.76],
  [0.76, 1.01]
]
const HOURS_BEFORE = STATES[0]

const hoursAt = (l) => {
  let h = STATES[0]
  MOVES.forEach(([a, b], k) => {
    const t = smooth(seg(l, a, b))
    h = h.map((v, i) => v + (STATES[k + 1][i] - STATES[k][i]) * t)
  })
  return h
}

/** Eine Recruiting-Woche: Aivy übernimmt die grauen Blöcke, die Zeit geht an Menschen. */
export function Week() {
  const c = useCopy('woche')
  const ui = useCopy('ui')
  const refs = useRef({ blocks: [], vals: [] })
  const autoRef = useRef(null)
  const autoNum = useRef(null)
  const humanNum = useRef(null)

  useFrame((l) => {
    const h = hoursAt(l)
    let auto = 0
    let human = 0
    BLOCKS.forEach(([, kind], i) => {
      const block = refs.current.blocks[i]
      const val = refs.current.vals[i]
      if (block) block.style.flexGrow = Math.max(0.0001, h[i]).toFixed(3)
      if (val) val.textContent = String(Math.round(h[i]))
      if (kind === 'admin') auto += HOURS_BEFORE[i] - h[i]
      if (kind === 'human') human += h[i]
    })
    if (autoRef.current)
      autoRef.current.style.width = `${((auto / 40) * 100).toFixed(2)}%`
    if (autoNum.current) autoNum.current.textContent = String(Math.round(auto))
    if (humanNum.current)
      humanNum.current.textContent = String(Math.round(human))
  })

  return (
    <div className='ba-week'>
      <div className='ba-week__copy'>
        <span className='ap-eyebrow'>{c.eyebrow}</span>
        <h2 className='ap-h1 ba-week__h'>
          <span style={anim({ at: [-1, 0], out: [0.14, 0.2], y: 0 })}>
            {c.before}
          </span>
          <span style={anim({ at: [0.76, 0.82], y: 16 })}>{c.after}</span>
        </h2>
        <div className='ba-week__steps'>
          {c.steps.map((s, i) => (
            <div
              key={s.title}
              className='ba-step'
              style={anim({
                at: [STEP_WIN[i][0], STEP_WIN[i][0] + 0.05],
                out:
                  i < 3 ? [STEP_WIN[i][1] - 0.04, STEP_WIN[i][1]] : undefined,
                y: 24,
                oy: -24
              })}
            >
              <span className='ba-step__n'>{`0${i + 1}`}</span>
              <strong>{s.title}</strong>
              <p>{s.text}</p>
            </div>
          ))}
        </div>
      </div>

      <div className='ba-week__viz'>
        <div className='ba-meters'>
          <span className='ba-meter ba-meter--auto'>
            <Shape kind='square' color={AUTO} size={16} />
            <b ref={autoNum}>0</b>
            <span>{c.hours}</span>
            <small>{c.auto}</small>
          </span>
          <span className='ba-meter ba-meter--human'>
            <Shape kind='circle' color={HUMAN} size={16} />
            <b ref={humanNum}>4</b>
            <span>{c.hours}</span>
            <small>{c.human}</small>
          </span>
        </div>
        <div className='ba-bar'>
          {BLOCKS.map(([id, kind], i) => (
            <span
              key={id}
              ref={(el) => (refs.current.blocks[i] = el)}
              className={`ba-bar__b ba-bar__b--${kind}`}
              style={{ flexGrow: HOURS_BEFORE[i] || 0.0001 }}
            />
          ))}
        </div>
        <div className='ba-auto'>
          <span ref={autoRef} className='ba-auto__fill' />
        </div>
        <ul className='ba-legend'>
          {BLOCKS.map(([id, kind], i) => (
            <li key={id} className={`ba-legend__i ba-legend__i--${kind}`}>
              <i />
              <span>{c.blocks[id]}</span>
              <b>
                <span ref={(el) => (refs.current.vals[i] = el)}>
                  {HOURS_BEFORE[i]}
                </span>{' '}
                {c.hours}
              </b>
            </li>
          ))}
        </ul>
        <span className='ba-foot'>{ui.illustrative}</span>
      </div>
    </div>
  )
}

/** Wo Automatisierung menschlicher macht, mit Belegen aus dem Kundenalltag. */
export function MoreHuman() {
  const c = useCopy('menschlicher')
  const shapes = ['circle', 'half', 'tri']
  return (
    <div className='ba-human'>
      <h2 className='ap-h1'>{c.title}</h2>
      <div className='ba-human__grid'>
        {c.points.map((p, i) => (
          <article
            key={p.title}
            className='ba-card'
            style={anim({ at: stagger(0.1, 0.12, 0.1, i), y: 40, s: 0.96 })}
          >
            <Shape kind={shapes[i]} color={HUMAN} size={30} />
            <strong>{p.title}</strong>
            <p>{p.text}</p>
            <span className='ba-card__src'>{p.source}</span>
          </article>
        ))}
      </div>
    </div>
  )
}

/** Mensch setzt die Messlatte, Aivy hält sie ein, Mensch entscheidet. */
export function Chain() {
  const c = useCopy('kette')
  const tones = ['human', 'auto', 'human']
  const shapes = ['circle', 'square', 'circle']
  return (
    <div className='ba-chain'>
      <h2 className='ap-h1'>{c.title}</h2>
      <div className='ba-chain__row'>
        {c.nodes.map((n, i) => (
          <div key={n.title} className='ba-chain__cell'>
            <article
              className={`ba-node ba-node--${tones[i]}`}
              style={anim({ at: stagger(0.08, 0.16, 0.1, i), y: 30 })}
            >
              <span className='ba-node__who'>
                <Shape
                  kind={shapes[i]}
                  color={tones[i] === 'auto' ? AUTO : HUMAN}
                  size={16}
                />
                {n.who}
              </span>
              <strong>{n.title}</strong>
              <p>{n.text}</p>
            </article>
            {i < 2 && (
              <span
                className='ba-chain__link'
                style={pv('k', stagger(0.16, 0.16, 0.1, i))}
              />
            )}
          </div>
        ))}
      </div>
      <div className='ba-note' style={anim({ at: [0.62, 0.7], y: 20 })}>
        <Shape kind='tri' color={C.yellow} size={18} />
        <span>
          {c.note}
          <small>{c.noteSource}</small>
        </span>
      </div>
    </div>
  )
}

/** Drei Zahlen aus dem Alltag. */
export function Proof() {
  const c = useCopy('belege')
  return (
    <div className='ba-proof'>
      <h2 className='ap-h1'>{c.title}</h2>
      <div className='ba-proof__grid'>
        {c.items.map((it, i) => (
          <div
            key={it.label}
            className='ba-proof__i'
            style={anim({ at: stagger(0.1, 0.12, 0.1, i), y: 30 })}
          >
            <span className='ba-proof__v'>{it.value}</span>
            <span className='ba-proof__l'>{it.label}</span>
            <span className='ba-proof__s'>{it.source}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

/** Abschluss mit beiden CTAs. */
export function Start() {
  const c = useCopy('start')
  const ui = useCopy('ui')
  return (
    <div className='ba-start'>
      <h2 className='ap-display'>
        <span className='ba-blue'>{c.title[0]}</span>
        <span className='ba-pink'>{c.title[1]}</span>
      </h2>
      <p className='ap-lead'>{c.sub}</p>
      <CtaPair ui={ui} size='xl' />
    </div>
  )
}
